import React, { createContext } from "react";
import { Services } from "./Services";

/**
 * React Context for providing services to the entire application.
 *
 * Services are instantiated once at application startup and provided through this context.
 * This enables dependency injection without global variables or prop drilling.
 *
 * @example
 * ```tsx
 * import { ServicesProvider } from '@/core/services/ServicesContext';
 *
 * const services = createServices();
 *
 * root.render(
 *   <ServicesProvider services={services}>
 *     <App />
 *   </ServicesProvider>
 * );
 * ```
 */
export const ServicesContext = createContext<Services | null>(null);

interface ServicesProviderProps {
  children: React.ReactNode;
  services: Services;
}

/**
 * Provider component that makes services available to all child components via ServicesContext.
 *
 * The services object is typically instantiated once at application startup and never changes,
 * so context consumers will not re-render unless a new services object is provided.
 *
 * @param props.services - Services object created via createServices()
 * @param props.children - Child components that will have access to services
 */
export function ServicesProvider({
  children,
  services,
}: ServicesProviderProps) {
  return (
    <ServicesContext.Provider value={services}>
      {children}
    </ServicesContext.Provider>
  );
}
